import { useState, useContext } from "react";

import {
  Box,
  Avatar,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  useTheme,
  Tooltip,
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTree,
  faBolt,
  faCircleQuestion,
  faBan,
} from "@fortawesome/pro-solid-svg-icons";

import { DefaultPaper } from "@aclymatepackages/atoms";
import { getYearlyTonsCo2eValues } from "@aclymatepackages/calcs/myAclymate";
import { nameToFirstName } from "@aclymatepackages/converters";
import { formatDollars } from "@aclymatepackages/formatters";

import Backdrop from "../atoms/mui/Backdrop";
import GrayPaper from "../atoms/GrayPaper";
import MyAclymateReportPaper from "../atoms/myAclymate/MyAcymateReportPaper";

import PortfolioCard from "../modules/projects/PortfolioCard";
import CnaughtOffsetsDisplayBlock from "../modules/projects/CnaughtOffsetsDisplayBlock";
import CnaughtPortfolioDetailsSlider from "../modules/projects/CnaughtPortfolioDetailsSlider";

import { fetchOurApi } from "../../helpers/utils/apiCalls";
import { StripeCustomerContext } from "../../helpers/contexts/stripeCustomer";
import { useLayoutHelpers } from "../../helpers/otherHelpers";
import { useCNaughtPortfolios } from "../../helpers/components/projects";

const buildPriceOptions = (yearlyTotalTonsCo2e, displayPricePerTon) => {
  const totalMonthlyCost = (yearlyTotalTonsCo2e / 12) * displayPricePerTon;

  return [
    {
      name: "base",
      percentage: 10,
      price: totalMonthlyCost * 0.1,
    },
    { name: "leader", percentage: 50, price: totalMonthlyCost * 0.5 },
    { name: "champion", percentage: 100, price: totalMonthlyCost },
  ];
};

const PriceOption = ({
  selectedPrice,
  price,
  percentage,
  name = "",
  onSelectPrice,
}) => (
  <Grid item sm={4} xs={8}>
    <Grid container direction="column" spacing={1}>
      <Grid item>
        {onSelectPrice ? (
          <FormControl>
            <FormControlLabel
              control={
                <Radio
                  checked={selectedPrice?.price === price}
                  onChange={onSelectPrice}
                />
              }
              label={name.toUpperCase()}
            />
          </FormControl>
        ) : (
          <Typography variant="h4">{name.toUpperCase()}</Typography>
        )}
      </Grid>
      <Grid item>
        <Typography variant="h1" display="inline">
          ${Math.round(price)}
        </Typography>
        <Typography variant="h4" color="textSecondary" display="inline">
          /mo
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="caption">
          *Covers about {percentage}% of your total footprint
        </Typography>
      </Grid>
    </Grid>
  </Grid>
);

const PriceSelectionBlock = ({
  yearlyTotalTonsCo2e,
  selectedPrice,
  setSelectedPrice,
  displayPricePerTon,
}) => {
  const { isMobile } = useLayoutHelpers();

  const priceOptions = buildPriceOptions(
    yearlyTotalTonsCo2e,
    displayPricePerTon
  );

  return (
    <GrayPaper>
      <Grid
        id="price-options-container"
        container
        spacing={2}
        justifyContent="center"
        wrap={isMobile ? "wrap" : "nowrap"}
      >
        {priceOptions.map((price, idx) => (
          <PriceOption
            key={`project-price-option-${idx}`}
            {...price}
            selectedPrice={selectedPrice}
            onSelectPrice={() => setSelectedPrice(price)}
          />
        ))}
      </Grid>
    </GrayPaper>
  );
};

const OffsetBucketsPurchaseInterface = ({
  setShowPortfolioDetails,
  yearlyTotalTonsCo2e,
  cNaughtPortfolios,
  setSubscriptionData,
  myAclymateSubscription,
  setUpdateSubscriptionData,
  name,
  email,
}) => {
  const { isMobile } = useLayoutHelpers();
  const { subscriptionId } = myAclymateSubscription || {};

  const { palette } = useTheme();

  const [selectedOffsetBucketIdx, setSelectedOffsetBucketIdx] = useState(0);
  const [selectedPrice, setSelectedPrice] = useState(null);

  const roundedYearlyTonsCo2e = Math.ceil(yearlyTotalTonsCo2e * 10) / 10;
  const { displayPricePerTon, name: portfolioName } =
    cNaughtPortfolios[selectedOffsetBucketIdx] || {};
  const { percentage, name: subscriptionName, price } = selectedPrice || {};

  const cancelSubscription = async () => {
    fetchOurApi({
      path: `/stripe/cancel-subscription`,
      method: "POST",
      data: {
        subscriptionId,
      },
    });
    return fetchOurApi({
      path: "/emails/myAclymate-subscription-cancel",
      method: "POST",
      data: {
        email,
        firstName: nameToFirstName(name),
        percentage,
        subscriptionName,
        portfolioName,
        monthlyPrice: formatDollars(price),
      },
    });
  };

  const CancelButton = () => {
    const onCancel = () => {
      cancelSubscription();
      return window.location.reload();
    };

    return (
      <>
        {isMobile ? (
          <Tooltip title="Cancel Subscription">
            <IconButton onClick={onCancel}>
              <FontAwesomeIcon icon={faBan} />
            </IconButton>
          </Tooltip>
        ) : (
          <Button onClick={onCancel}>Cancel Subscription</Button>
        )}
      </>
    );
  };

  const onClickSubscribe = async () => {
    if (setUpdateSubscriptionData) {
      setUpdateSubscriptionData(false);
    }

    if (myAclymateSubscription) {
      await cancelSubscription();
    }

    return setSubscriptionData({
      price: selectedPrice,
      portfolio: cNaughtPortfolios[selectedOffsetBucketIdx],
    });
  };

  const offsetPurchaseSteps = [
    {
      title: "Choose your project portfolio",
      content: (
        <Grid item xs={12}>
          <CnaughtOffsetsDisplayBlock
            selectedOffsetBucketIdx={selectedOffsetBucketIdx}
            cNaughtPortfolios={cNaughtPortfolios}
            setSelectedOffsetBucketIdx={setSelectedOffsetBucketIdx}
            setShowPortfolioDetails={setShowPortfolioDetails}
          />
        </Grid>
      ),
      action: subscriptionId && <CancelButton />,
    },
    {
      title: "Choose your subscription level",
      content: (
        <Grid item xs={12}>
          <PriceSelectionBlock
            selectedPrice={selectedPrice}
            setSelectedPrice={setSelectedPrice}
            yearlyTotalTonsCo2e={roundedYearlyTonsCo2e}
            displayPricePerTon={displayPricePerTon}
          />
        </Grid>
      ),
    },
    {
      title: "Subscribe!",
      action: (
        <Grid item sm={8} xs={6} container justifyContent="center">
          <Grid item>
            <Button
              disabled={!selectedPrice}
              variant="contained"
              color="secondary"
              onClick={onClickSubscribe}
            >
              Subscribe
            </Button>
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <MyAclymateReportPaper
      title="Offset Your Carbon Footprint"
      subtitle="Reduce your impact by supporting verified carbon offset projects"
    >
      <Box display="flex" flexDirection="column" rowGap={4}>
        {offsetPurchaseSteps.map(({ title, content, action }, idx) => (
          <Grid
            key={`purchase-step-${idx}`}
            container
            spacing={1}
            justifyContent="space-between"
          >
            <Grid item xs={12}>
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <Avatar sx={{ bgcolor: palette.primary.main }}>
                        <Typography variant="h3" align="center">
                          {idx + 1}
                        </Typography>
                      </Avatar>
                    </Grid>
                    <Grid item>
                      <Typography variant="subtitle1">{title}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
                {action}
              </Grid>
            </Grid>
            {content}
          </Grid>
        ))}
      </Box>
    </MyAclymateReportPaper>
  );
};

const ExistingSubscriptionDisplayBlock = ({
  subscription,
  setShowPortfolioDetails,
  yearlyTotalTonsCo2e,
  setUpdateSubscriptionData,
}) => {
  const { portfolio, metadata } = subscription;
  const { displayPricePerTon } = portfolio || {};
  const { percentage } = metadata;

  const { palette } = useTheme();

  const priceOptions = buildPriceOptions(
    yearlyTotalTonsCo2e,
    displayPricePerTon
  );

  const selectedPrice = priceOptions.find(
    (price) => price.percentage === Number(percentage)
  );

  const displayBlocks = [
    {
      icon: faTree,
      title: "Your Offset Portfolio",
      content: (
        <PortfolioCard
          portfolio={portfolio}
          setShowPortfolioDetails={setShowPortfolioDetails}
        />
      ),
    },
    {
      icon: faBolt,
      title: "Your Subscription Tier",
      content: <PriceOption {...selectedPrice} />,
    },
    {
      icon: faCircleQuestion,
      title: "Update Subscription",
      content: (
        <Box
          p={2}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          rowGap={2}
        >
          <Button
            variant="contained"
            onClick={() => setUpdateSubscriptionData(true)}
          >
            Update Subscription
          </Button>
          <Typography variant="body2" align="center">
            Choose a different offset portfolio to support, change your
            suscription tier, or cancel your subscription.
          </Typography>
          <Typography variant="caption" align="center" color="textSecondary">
            Please note: When you update your subscription, you’ll receive two
            emails - one to confirm cancellation of your previous subscription
            and another to confirm your new subscription.
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <MyAclymateReportPaper
      title="Your Offset Subscription"
      subtitle="You are reducing your impact by supporting verified carbon offset projects"
    >
      <Grid container spacing={2} alignItems="stretch">
        {displayBlocks.map(({ icon, title, content, component }, idx) => (
          <Grid item sm={4} xs={12} key={`subscription-display-block-${idx}`}>
            <Box
              display="flex"
              flexDirection="column"
              rowGap={2}
              sx={{ height: "100%" }}
            >
              <Grid item container alignItems="center" spacing={2}>
                <Grid item>
                  <Avatar sx={{ bgcolor: palette.primary.main }}>
                    <FontAwesomeIcon icon={icon} />
                  </Avatar>
                </Grid>
                <Grid item>
                  <Typography variant="h6">{title}:</Typography>
                </Grid>
              </Grid>
              <GrayPaper style={{ height: "100%" }}>{content}</GrayPaper>
            </Box>
          </Grid>
        ))}
      </Grid>
    </MyAclymateReportPaper>
  );
};

const OffsetBucketsBox = ({
  emissionsData,
  setSubscriptionData,
  name,
  email,
}) => {
  const [cNaughtPortfolios, cNaughtPortfoliosLoading] = useCNaughtPortfolios();

  const [updateSubscriptionData, setUpdateSubscriptionData] = useState(false);
  const [showPortfolioDetails, setShowPortfolioDetails] = useState(null);

  const { yearlyTotalTonsCo2e } = getYearlyTonsCo2eValues(emissionsData);
  const { subscriptions, isSubscriptionsLoading } = useContext(
    StripeCustomerContext
  );

  const findMyAclymateSubscriptionInfo = () => {
    if (isSubscriptionsLoading || !subscriptions.length) {
      return null;
    }

    const myAclymateSubscription = subscriptions.find(
      ({ product }) => product === "myAclymate"
    );

    if (!myAclymateSubscription) {
      return null;
    }

    const { status } = myAclymateSubscription;
    if (status !== "active") {
      return null;
    }

    const subscriptionPortfolio = cNaughtPortfolios.find(
      ({ stripePriceId }) =>
        stripePriceId === myAclymateSubscription.subscriptionPriceId
    );

    return { ...myAclymateSubscription, portfolio: subscriptionPortfolio };
  };

  const myAclymateSubscription = findMyAclymateSubscriptionInfo();

  return (
    <>
      {updateSubscriptionData && (
        <Backdrop
          open={updateSubscriptionData}
          onClose={() => setUpdateSubscriptionData(false)}
          style={{ overflow: "auto", zIndex: 900 }}
        >
          <Box p={2}>
            <OffsetBucketsPurchaseInterface
              setShowPortfolioDetails={setShowPortfolioDetails}
              yearlyTotalTonsCo2e={yearlyTotalTonsCo2e}
              cNaughtPortfolios={cNaughtPortfolios}
              setSubscriptionData={setSubscriptionData}
              myAclymateSubscription={myAclymateSubscription}
              setUpdateSubscriptionData={setUpdateSubscriptionData}
              name={name}
              email={email}
            />
          </Box>
        </Backdrop>
      )}
      {showPortfolioDetails && (
        <CnaughtPortfolioDetailsSlider
          onClose={() => setShowPortfolioDetails(null)}
          {...showPortfolioDetails}
        />
      )}
      {cNaughtPortfoliosLoading ? (
        <DefaultPaper style={{ width: "100%" }}>
          <Grid container item justifyContent="center">
            <CircularProgress />
          </Grid>
        </DefaultPaper>
      ) : myAclymateSubscription ? (
        <ExistingSubscriptionDisplayBlock
          subscription={myAclymateSubscription}
          yearlyTotalTonsCo2e={yearlyTotalTonsCo2e}
          setShowPortfolioDetails={setShowPortfolioDetails}
          setUpdateSubscriptionData={setUpdateSubscriptionData}
        />
      ) : (
        <OffsetBucketsPurchaseInterface
          setShowPortfolioDetails={setShowPortfolioDetails}
          yearlyTotalTonsCo2e={yearlyTotalTonsCo2e}
          cNaughtPortfolios={cNaughtPortfolios}
          setSubscriptionData={setSubscriptionData}
          myAclymateSubscription={myAclymateSubscription}
          name={name}
          email={email}
        />
      )}
    </>
  );
};

export default OffsetBucketsBox;
